import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { WHATSAPP_HREF } from "@/lib/links";
import { trackWhatsAppClick } from "@/lib/analytics";
import { DB } from "@/lib/media";

const NAV_LINKS = [
    { to: "/services", label: "Services" },
    { to: "/work", label: "Work" },
    { to: "/about", label: "About" },
    { to: "/contact", label: "Contact" },
];

export default function Nav() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const { pathname } = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    // Close mobile menu whenever the route changes
    useEffect(() => {
        setOpen(false);
    }, [pathname]);

    return (
        <header
            data-testid="site-nav"
            className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
                scrolled
                    ? "bg-bone/85 backdrop-blur-md border-b border-black/5"
                    : "bg-transparent"
            }`}
        >
            <div className="mx-auto max-w-[1400px] px-6 lg:px-10 h-20 flex items-center justify-between">
                <Link
                    to="/"
                    data-testid="nav-logo"
                    className="flex items-center gap-3"
                    aria-label="Digitalbeej — home"
                >
                    <img
                        src={DB.logoBlack}
                        alt="Digitalbeej"
                        className="h-11 lg:h-12 w-auto object-contain"
                        fetchPriority="high"
                        decoding="async"
                    />
                    <span className="hidden md:inline text-[10px] uppercase tracking-[0.22em] font-bold text-black/45 border-l border-black/15 pl-3">
                        Modern Creative Studio
                    </span>
                </Link>

                <nav className="hidden md:flex items-center gap-7 lg:gap-9">
                    {NAV_LINKS.map((link) => {
                        const active = pathname.startsWith(link.to);
                        return (
                            <Link
                                key={link.to}
                                to={link.to}
                                data-testid={`nav-link-${link.to.slice(1)}`}
                                className={`text-sm font-medium link-underline tracking-tight ${
                                    active ? "text-[#2E5019]" : ""
                                }`}
                            >
                                {link.label}
                            </Link>
                        );
                    })}
                </nav>

                <div className="flex items-center gap-3">
                    <a
                        data-testid="nav-cta-whatsapp"
                        href={WHATSAPP_HREF}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() =>
                            trackWhatsAppClick({
                                section: "header",
                                buttonName: "nav_whatsapp",
                            })
                        }
                        className="group relative inline-flex items-center gap-2 bg-obsidian text-bone px-5 py-2.5 text-xs uppercase tracking-[0.18em] font-bold hover:bg-olive hover:text-obsidian transition-colors duration-300"
                    >
                        <span
                            aria-hidden
                            className="inline-block w-1.5 h-1.5 bg-olive-mint rounded-full"
                        />
                        WhatsApp
                        <span className="inline-block transition-transform duration-300 group-hover:translate-x-1">
                            →
                        </span>
                    </a>

                    <button
                        data-testid="nav-mobile-toggle"
                        aria-label="Toggle menu"
                        onClick={() => setOpen((v) => !v)}
                        className="md:hidden inline-flex flex-col gap-1 p-2"
                    >
                        <span
                            className={`block w-5 h-px bg-obsidian transition-transform ${
                                open ? "translate-y-1 rotate-45" : ""
                            }`}
                        />
                        <span
                            className={`block w-5 h-px bg-obsidian transition-transform ${
                                open ? "-translate-y-px -rotate-45" : ""
                            }`}
                        />
                    </button>
                </div>
            </div>

            {/* Mobile menu */}
            {open && (
                <div
                    data-testid="nav-mobile-menu"
                    className="md:hidden border-t border-black/10 bg-bone/95 backdrop-blur"
                >
                    <div className="px-6 py-6 flex flex-col gap-5">
                        {NAV_LINKS.map((link) => (
                            <Link
                                key={link.to}
                                to={link.to}
                                data-testid={`nav-mobile-link-${link.to.slice(1)}`}
                                className="text-left text-lg font-display font-bold tracking-tight"
                            >
                                {link.label}
                            </Link>
                        ))}
                    </div>
                </div>
            )}
        </header>
    );
}
